import { Link } from 'react-router-dom';
import { CATEGORY_ORDER } from './library';
import { ALL_KINDS, KINDS, type ConceptKind } from './kinds';
import { useAddons } from '../games/host/addons';
import type { ConceptCategory } from './types';
import type { LibraryModel } from './useLibrary';
import { codeOf } from './registry';
import { explainCode } from './programCode';
import { useT } from '../i18n/i18n';
import { setInTrial, useTrial } from '../subscription/trial';

function CodeChip({ code, title }: { code: string; title: string }) {
  const parts = code.split('-');
  const main = parts.slice(0, 2).join('-');
  const rest = parts.slice(2).join('-');
  return (
    <code className="library-code" title={title}>
      <span>{main}</span>
      {rest && <><wbr /><span>-{rest}</span></>}
    </code>
  );
}

export default function ClassicLibrary({ lib }: { lib: LibraryModel }) {
  useAddons();
  const t = useT();
  const { trial, inTrial } = useTrial();
  return (
    <div>
      <div className="banner" role="region" aria-label={t('Library')}>
        <strong>{t('Library.')}</strong>
        <span className="muted">
          {' '}
          {t('{n} ready-made programs. Try one first, or add it to your programs and change the words later. Difficulty is our rating out of 10, as a guide only.', { n: lib.total })}
        </span>{' '}
        <Link to="/request-game">{t("Can't find what you need? Request a custom game")}</Link>
      </div>

      {lib.actionError && <div className="error" role="alert">{lib.actionError}</div>}
      {lib.ratingsError && <div className="error" role="alert">{lib.ratingsError}</div>}

      <div className="row library-filters">
        <div className="field">
          <label htmlFor="library-search">{t('Search')}</label>
          <input
            id="library-search"
            type="text"
            value={lib.query}
            onChange={(e) => lib.setQuery(e.target.value)}
            placeholder={t('e.g. colours, shops, or a code like REC-COLOR')}
          />
        </div>
        <div className="field">
          <label htmlFor="library-category">{t('Category')}</label>
          <select
            id="library-category"
            value={lib.category}
            onChange={(e) => lib.setCategory(e.target.value as ConceptCategory | '')}
          >
            <option value="">{t('All categories')}</option>
            {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{t(c)}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="library-kind">{t('Type of activity')}</label>
          <select
            id="library-kind"
            value={lib.kind}
            onChange={(e) => lib.setKind(e.target.value as ConceptKind | '')}
          >
            <option value="">{t('All types')}</option>
            {ALL_KINDS.map((k) => <option key={k} value={k}>{t(KINDS[k].label)}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="library-sort">{t('Sort by')}</label>
          <select
            id="library-sort"
            value={lib.sort}
            onChange={(e) => lib.setSort(e.target.value as typeof lib.sort)}
          >
            <option value="category">{t('Category')}</option>
            <option value="easiest">{t('Difficulty — easiest first')}</option>
            <option value="hardest">{t('Difficulty — hardest first')}</option>
          </select>
        </div>
      </div>

      <p className="field-hint" aria-live="polite">
        {lib.rows.length === lib.total ? t('{n} programs', { n: lib.rows.length }) : t('{n} of {total} programs', { n: lib.rows.length, total: lib.total })}
      </p>

      {lib.rows.length === 0 ? (
        <p className="muted">{t('Nothing matches.')}</p>
      ) : (
        <div className="card library-card">
          <table className="library-table has-code">
            <thead>
              <tr>
                <th scope="col">{t('Code')}</th>
                <th scope="col">{t('Program')}</th>
                <th scope="col">{t('Category')}</th>
                <th scope="col">{t('Type')}</th>
                <th scope="col">{t('Difficulty')}</th>
                <th scope="col"><span className="visually-hidden">{t('Actions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {lib.rows.map((c) => {
                const programId = lib.added[c.id];
                const locked = !!trial?.limited && !inTrial(c.id);
                return (
                  <tr key={c.id}>
                    <td data-label={t('Code')}><CodeChip code={codeOf(c.id)} title={explainCode(codeOf(c.id), c.label, c.category, c.kind)} /></td>
                    <td>
                      <span className="library-name">{c.label}</span>
                      <span className="library-desc">{t(c.description)}</span>
                      {lib.isAdmin && (
                        <label className="library-trial">
                          <input type="checkbox" checked={inTrial(c.id)} onChange={(e) => void setInTrial(c.id, c.kind, e.target.checked)} />
                          <span>{t('In the free trial')}</span>
                        </label>
                      )}
                    </td>
                    <td data-label={t('Category')}>{t(c.category)}</td>
                    <td data-label={t('Type')}>{t(KINDS[c.kind].label)}</td>
                    <td data-label={t('Difficulty')}>
                      {lib.isAdmin ? (
                        <div className="field" style={{ margin: 0 }}>
                          <select
                            aria-label={t('Difficulty of {program}, out of 10', { program: c.label })}
                            value={lib.difficultyOf(c.id) ?? ''}
                            onChange={(e) => void lib.rate(c.id, e.target.value ? Number(e.target.value) : null)}
                          >
                            <option value="">{t('Not rated')}</option>
                            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{t('{n} / 10', { n })}</option>)}
                          </select>
                        </div>
                      ) : lib.difficultyOf(c.id) !== null ? (
                        t('{n} / 10', { n: lib.difficultyOf(c.id) ?? '' })
                      ) : (
                        <span className="muted">{t('Not rated')}</span>
                      )}
                    </td>
                    <td>
                      <div className="library-actions">
                        {programId && (
                          <>
                            <span className="library-added" role="status">✓ {t('Added')}</span>
                            <Link className="btn ghost" to={`/programs/${programId}/edit`}>{t('Edit')}</Link>
                          </>
                        )}
                        {locked ? (
                          <span className="library-locked">{t('In the full trial')}</span>
                        ) : (
                        <button
                          type="button"
                          className="btn"
                          disabled={lib.busyId === c.id}
                          aria-label={t('Add {program} to my programs', { program: c.label })}
                          onClick={() => void lib.add(c)}
                        >
                          {lib.busyId === c.id ? t('Adding…') : programId ? t('Add again') : t('Add')}
                        </button>
                        )}
                        <Link className="btn secondary" to={`/library/${c.id}`}>{t('Try it')}</Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
